import { useState, useCallback } from 'react'
import { useMutation } from '@tanstack/react-query'
import dayjs from 'dayjs'
import documentService from '@api/v1/documentService'

const FILE_ICONS = {
  pdf: { bg: 'bg-red-100 text-red-600', label: 'PDF' },
  doc: { bg: 'bg-blue-100 text-blue-600', label: 'Word' },
  docx: { bg: 'bg-blue-100 text-blue-600', label: 'Word' },
  xls: { bg: 'bg-green-100 text-green-600', label: 'Excel' },
  xlsx: { bg: 'bg-green-100 text-green-600', label: 'Excel' },
  csv: { bg: 'bg-green-100 text-green-600', label: 'CSV' },
  ppt: { bg: 'bg-orange-100 text-orange-600', label: 'PPT' },
  pptx: { bg: 'bg-orange-100 text-orange-600', label: 'PPT' },
  txt: { bg: 'bg-gray-100 text-gray-600', label: 'Text' },
  msg: { bg: 'bg-purple-100 text-purple-600', label: 'Email' },
  zip: { bg: 'bg-yellow-100 text-yellow-700', label: 'ZIP' },
  jpg: { bg: 'bg-pink-100 text-pink-600', label: 'Image' },
  jpeg: { bg: 'bg-pink-100 text-pink-600', label: 'Image' },
  png: { bg: 'bg-pink-100 text-pink-600', label: 'Image' },
}

function getFileType(name) {
  if (!name) return { bg: 'bg-gray-100 text-gray-500', label: 'File' }
  const ext = name.split('.').pop()?.toLowerCase()
  return FILE_ICONS[ext] || { bg: 'bg-gray-100 text-gray-500', label: ext?.toUpperCase() || 'File' }
}

function isViewable(name) {
  if (!name) return false
  const ext = name.split('.').pop()?.toLowerCase()
  return ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'txt'].includes(ext)
}

/**
 * Reusable expandable document card with file view + AI summary.
 * Used in /documents page and Matter Detail Documents tab.
 */
const DocumentCard = ({ doc, defaultExpanded = false }) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)
  const [activeTab, setActiveTab] = useState('details')
  const [summary, setSummary] = useState(doc.summary || null)
  const [summaryError, setSummaryError] = useState(null)
  const [fileBlobUrl, setFileBlobUrl] = useState(null)
  const [fileLoading, setFileLoading] = useState(false)
  const [fileError, setFileError] = useState(null)

  const fileType = getFileType(doc.name)
  const canView = doc.hasFile && isViewable(doc.name)

  /** Fetch file as authenticated blob URL (for embed/img/download) */
  const loadFile = useCallback(async () => {
    if (fileBlobUrl || fileLoading || !doc.hasFile) return
    setFileLoading(true)
    setFileError(null)
    try {
      const url = await documentService.getFileBlobUrl(doc.id, doc.name)
      setFileBlobUrl(url)
    } catch (err) {
      setFileError(err.response?.status === 404 ? 'File not available' : 'Failed to load file')
    } finally {
      setFileLoading(false)
    }
  }, [doc.id, doc.hasFile, fileBlobUrl, fileLoading])

  const summaryMutation = useMutation({
    mutationFn: () => documentService.getSummary(doc.id),
    onSuccess: (text) => {
      setSummary(text)
      setSummaryError(null)
    },
    onError: (err) => {
      setSummaryError(err.message || 'Failed to generate summary')
    },
  })

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden transition-shadow hover:shadow-md">
      {/* Collapsed row */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-5 py-3.5 flex items-center gap-4 text-left"
      >
        {/* File type icon */}
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${fileType.bg}`}>
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
          </svg>
        </div>

        {/* Name + badges */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{doc.name}</p>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${fileType.bg}`}>
              {fileType.label}
            </span>
            {doc.hasFile && (
              <span className="text-[10px] font-medium text-cb-teal-dark bg-cb-teal/10 px-1.5 py-0.5 rounded">
                Has File
              </span>
            )}
            {doc.category && (
              <span className="text-xs text-gray-500">{doc.category}</span>
            )}
            {doc.owner && (
              <span className="text-xs text-gray-500">{doc.owner}</span>
            )}
          </div>
        </div>

        {/* Date */}
        <div className="text-right shrink-0">
          <p className="text-xs text-gray-500">
            {doc.fileDate ? dayjs(doc.fileDate).format('M/D/YYYY') : doc.createdDate ? dayjs(doc.createdDate).format('M/D/YYYY') : ''}
          </p>
        </div>

        {/* Chevron */}
        <svg
          className={`w-4 h-4 text-gray-500 shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
      </button>

      {/* Expanded detail */}
      {isExpanded && (
        <div className="border-t border-gray-100">
          {/* Tabs */}
          <div className="flex gap-1 px-5 pt-3 border-b border-gray-100">
            {[
              { id: 'details', label: 'Details' },
              ...(doc.hasFile ? [{ id: 'preview', label: 'File Preview' }] : []),
              { id: 'summary', label: 'AI Summary' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors -mb-px ${
                  activeTab === tab.id
                    ? 'border-cb-accent text-cb-accent-text'
                    : 'border-transparent text-gray-500 hover:text-gray-600'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="px-5 py-4">
            {/* Details tab */}
            {activeTab === 'details' && (
              <div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                  <MetaCard label="Category" value={doc.category} />
                  <MetaCard label="Owner" value={doc.owner} />
                  <MetaCard label="Creator" value={doc.creator} />
                  <MetaCard label="File Date" value={doc.fileDate ? dayjs(doc.fileDate).format('MMM D, YYYY') : null} />
                </div>
                {doc.description && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Description</p>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{doc.description}</p>
                  </div>
                )}
                {doc.hasFile && (
                  <div className="mt-3">
                    {fileBlobUrl ? (
                      <a
                        href={fileBlobUrl}
                        download={doc.name}
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-cb-accent-text bg-cb-accent/10 rounded-lg hover:bg-cb-accent/20 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                        </svg>
                        Download File
                      </a>
                    ) : (
                      <button
                        onClick={loadFile}
                        disabled={fileLoading}
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-cb-accent-text bg-cb-accent/10 rounded-lg hover:bg-cb-accent/20 transition-colors disabled:opacity-50"
                      >
                        {fileLoading ? (
                          <div className="animate-spin w-4 h-4 border-2 border-cb-accent border-t-transparent rounded-full" />
                        ) : (
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                          </svg>
                        )}
                        {fileLoading ? 'Loading...' : 'Download File'}
                      </button>
                    )}
                    {fileError && <p className="text-xs text-red-500 mt-1">{fileError}</p>}
                  </div>
                )}
              </div>
            )}

            {/* File Preview tab */}
            {activeTab === 'preview' && doc.hasFile && (
              <div>
                {fileBlobUrl && canView ? (
                  <div className="rounded-lg border border-gray-100 overflow-hidden">
                    {doc.name.toLowerCase().endsWith('.pdf') ? (
                      <embed src={fileBlobUrl} type="application/pdf" className="w-full h-[32rem]" />
                    ) : (
                      <img src={fileBlobUrl} alt={doc.name} className="max-w-full max-h-[32rem] object-contain mx-auto" />
                    )}
                  </div>
                ) : fileBlobUrl && !canView ? (
                  <div className="p-8 bg-gray-50 rounded-lg text-center">
                    <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                    </svg>
                    <p className="text-sm text-gray-500 mb-2">Preview not available for this file type</p>
                    <a href={fileBlobUrl} download={doc.name} className="text-sm text-cb-accent-text hover:underline">
                      Download to view
                    </a>
                  </div>
                ) : fileLoading ? (
                  <div className="p-8 bg-gray-50 rounded-lg text-center">
                    <div className="animate-spin w-6 h-6 border-2 border-cb-accent border-t-transparent rounded-full mx-auto mb-3" />
                    <p className="text-sm text-gray-500">Loading file...</p>
                  </div>
                ) : fileError ? (
                  <div className="p-8 bg-red-50 rounded-lg text-center">
                    <p className="text-sm text-red-600 mb-2">{fileError}</p>
                    <button onClick={loadFile} className="text-sm text-cb-accent-text hover:underline">Retry</button>
                  </div>
                ) : (
                  <div className="p-8 bg-gray-50 rounded-lg text-center">
                    <button
                      onClick={loadFile}
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-cb-accent-text bg-cb-accent/10 rounded-lg hover:bg-cb-accent/20 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Load File Preview
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* AI Summary tab */}
            {activeTab === 'summary' && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-cb-accent-text" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
                    </svg>
                    <h3 className="text-sm font-semibold text-gray-900">AI Summary</h3>
                  </div>
                  <button
                    onClick={() => summaryMutation.mutate()}
                    disabled={summaryMutation.isPending}
                    className="px-3 py-1.5 text-xs font-medium text-cb-accent-text bg-cb-accent/10 rounded-lg hover:bg-cb-accent/20 transition-colors disabled:opacity-50"
                  >
                    {summaryMutation.isPending ? 'Generating...' : summary ? 'Regenerate' : 'Generate Summary'}
                  </button>
                </div>

                {summaryMutation.isPending && !summary && (
                  <div className="flex items-center gap-2 p-4 bg-gray-50 rounded-lg">
                    <div className="animate-spin w-4 h-4 border-2 border-cb-accent border-t-transparent rounded-full" />
                    <p className="text-sm text-gray-500">Generating AI summary...</p>
                  </div>
                )}

                {summaryError && (
                  <div className="p-4 bg-red-50 rounded-lg">
                    <p className="text-sm text-red-600">{summaryError}</p>
                  </div>
                )}

                {summary && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    {summary.split('\n').filter(Boolean).map((line, i) => (
                      <p key={i} className="text-sm text-gray-700 mb-2 last:mb-0">{line}</p>
                    ))}
                  </div>
                )}

                {!summary && !summaryMutation.isPending && !summaryError && (
                  <p className="text-xs text-gray-500 italic">
                    Click &quot;Generate Summary&quot; to get an AI-powered analysis of this document.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

const MetaCard = ({ label, value }) => (
  <div className="p-2.5 bg-gray-50 rounded-lg">
    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">{label}</p>
    <p className="text-sm text-gray-700 mt-0.5 truncate">{value || '—'}</p>
  </div>
)

export { getFileType, isViewable }
export default DocumentCard
